import React from 'react';
import { REVERT } from 'redux-optimistic-ui';
import get from 'lodash/get';
import { errorFlags, type FlagsMapper, type FlagComponentProps } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { CardCreateGlobalFlag } from '@atlassian/jira-software-flags/src/ui/card-create-global/index.tsx';
import { SHOW_ERROR_FLAG_FROM_SWAG } from '@atlassian/jira-software-swag/src/common/constants.tsx';
import { extractFlagFromAction } from '@atlassian/jira-software-swag/src/services/error-to-flag/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { CardIdentifiers, GlobalIssueCreateProps } from '../model/types.tsx';
import { BULK_OPS_SHOW_FAILED_FLAG } from '../state/actions/bulk-ops-flags/error/index.tsx';
import {
	CARD_CREATE_FILTERED,
	CARD_CREATE_UNMAPPED,
	CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK,
	CARD_CREATE_FILTERED_V2,
	CARD_CREATE_GLOBAL,
	CARD_CREATE_SPLIT_ISSUE,
} from '../state/actions/cards/create/index.tsx';
import {
	FILTERED_CARD_CLEAR_FAILURE,
	FILTERED_CARD_FAILURE,
	REFILTER_FAILURE,
} from '../state/actions/cards/filtered-cards/index.tsx';
import {
	CARD_DRAG_DISALLOWED,
	CARDS_MOVE_DISABLED,
	CARDS_TRANSITION_AND_RANK_FAILURE,
} from '../state/actions/cards/move/index.tsx';
import type { Action as CombinedActions } from '../state/actions/index.tsx';
import { FLAG_SPRINT_STARTED } from '../state/actions/sprints/start/index.tsx';
import type { State } from '../state/types.tsx';
import { SPRINT_BOARD_LINK_CLICK_PERMISSIONLESS } from '../state/actions/sprints/board-link-click/index.tsx';
import BulkActionsFailedFlag from './bulk-actions/error/index.tsx';
import CardCreateFilteredFlagV2 from './card-create-filtered-v2/index.tsx';
import CardCreateFilteredFlag from './card-create-filtered/index.tsx';
import CardCreateUnmappedWithIssueLinkFlag from './card-create-unmapped-with-issue-link/index.tsx';
import CardCreateUnmappedFlag from './card-create-unmapped/index.tsx';
import { CardDragDisallowedFlag } from './card-drag-disallowed/index.tsx';
import UnableToRankIssueFlag from './card-move-disabled/index.tsx';
import ClearCustomFilterFailed from './clear-custom-filter-failed/index.tsx';
import CustomFilterFailed from './custom-filter-failed/index.tsx';
import { SplitIssueSuccessFlag } from './split-issue-success/index.tsx';
import { showSprintStartedFlag } from './sprint-started-flag/index.tsx';
import { TransitionAndRankFailureFlag } from './transition-and-rank-failure/index.tsx';
import { SprintBoardPermissionlessFlag } from './sprint-board-permissionless-flag/index.tsx';

export const flagsMapper: FlagsMapper<CombinedActions, State> = (action) => {
	if (
		get(action, ['meta', 'optimistic', 'type'], null) === REVERT &&
		get(action, ['meta', 'showGenericFlag'], null) === true
	) {
		return errorFlags.genericError();
	}

	switch (action.type) {
		case CARD_CREATE_FILTERED:
			return (props: FlagComponentProps) => (
				// @ts-expect-error - TS2322 - Type '{ card: Card; id: string | number; onDismissed: ((id: string | number) => void) & ((id: string | number) => void); }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<Omit<{ onPrimaryActionClick: (cardId: any) => void; }, "onPrimaryActionClick">, any, any>> & Readonly<...> & Readonly<...>'.
				<CardCreateFilteredFlag {...props} card={action.payload} />
			);
		case CARD_CREATE_FILTERED_V2:
			return (props: FlagComponentProps) => (
				<CardCreateFilteredFlagV2 {...props} cardIds={action.payload} />
			);
		case CARD_DRAG_DISALLOWED:
			return (props: FlagComponentProps) => <CardDragDisallowedFlag {...props} />;
		case FLAG_SPRINT_STARTED:
			return showSprintStartedFlag();
		case SHOW_ERROR_FLAG_FROM_SWAG:
			return extractFlagFromAction(action);
		case CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK:
			return (props: FlagComponentProps) => (
				<CardCreateUnmappedWithIssueLinkFlag
					{...props}
					cardKeys={action.payload.cardsIdentifiers.map((cardIds: CardIdentifiers) => cardIds.key)}
					{...(fg('show-create-success-flag-once-in-backlog-page')
						? { isAutoDismissed: false }
						: {})}
				/>
			);
		case CARD_CREATE_UNMAPPED:
			return (props: FlagComponentProps) => {
				const {
					payload: { cardKeys, numIssues },
				} = action;
				if (cardKeys) {
					return <CardCreateUnmappedWithIssueLinkFlag {...props} cardKeys={cardKeys} />;
				}
				return <CardCreateUnmappedFlag {...props} numIssues={numIssues} />;
			};
		case FILTERED_CARD_FAILURE:
		case REFILTER_FAILURE:
			return (props: FlagComponentProps) => <CustomFilterFailed {...props} />;
		case FILTERED_CARD_CLEAR_FAILURE:
			return (props: FlagComponentProps) => <ClearCustomFilterFailed {...props} />;
		case CARD_CREATE_GLOBAL:
			return (props: FlagComponentProps) => (
				<CardCreateGlobalFlag
					{...props}
					cardKeys={action.payload.globalIssues.map(
						(issue: GlobalIssueCreateProps) => issue.issueKey,
					)}
					{...(fg('show-create-success-flag-once-in-backlog-page')
						? { isAutoDismissed: false }
						: {})}
				/>
			);
		case CARD_CREATE_SPLIT_ISSUE:
			return (props: FlagComponentProps) => (
				<SplitIssueSuccessFlag
					{...props}
					originalIssueKey={action.payload.originalIssueKey}
					issueKeys={action.payload.newIssueKeys}
				/>
			);
		case CARDS_MOVE_DISABLED:
			return (props: FlagComponentProps) => (
				<UnableToRankIssueFlag
					{...props}
					boardId={action.payload.boardId}
					isAdmin={action.payload.isAdmin}
				/>
			);
		case CARDS_TRANSITION_AND_RANK_FAILURE:
			return (props: FlagComponentProps) => (
				<TransitionAndRankFailureFlag {...props} errorMessages={action.payload.errorMessages} />
			);
		case BULK_OPS_SHOW_FAILED_FLAG:
			return (props: FlagComponentProps) => {
				const { payload } = action;
				return <BulkActionsFailedFlag {...props} {...payload} />;
			};
		case SPRINT_BOARD_LINK_CLICK_PERMISSIONLESS:
			return expValEquals('surface_origin_board_on_sprint_containers', 'cohort', 'test')
				? (props: FlagComponentProps) => {
						return <SprintBoardPermissionlessFlag {...props} />;
					}
				: undefined;
		default:
			return undefined;
	}
};
